import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Ban, Check, RotateCcw, Trash2 } from 'lucide-react';

import { adminApi, type AdminJobRow } from '@/api/admin.api';
import { useConfirm } from './ui/ConfirmDialog';

type BulkAction = 'close' | 'archive';

const STATUS_STYLES: Record<AdminJobRow['status'], string> = {
  DRAFT: 'bg-amber-50 text-amber-800 border-amber-200',
  OPEN: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  HOLD: 'bg-orange-50 text-orange-700 border-orange-200',
  IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200',
  CLOSED: 'bg-slate-100 text-slate-700 border-slate-200',
  ARCHIVED: 'bg-slate-50 text-slate-500 border-slate-200',
};

const CLOSE_REASONS = [
  { value: 'FILLED', label: 'Position filled' },
  { value: 'CANCELLED', label: 'Role cancelled' },
  { value: 'EXPIRED', label: 'Hiring window passed' },
  { value: 'DUPLICATE', label: 'Duplicate posting' },
  { value: 'OTHER', label: 'Other' },
];

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

/**
 * Platform-admin view for cleaning up stale job postings across every org.
 *
 * Close and archive are non-destructive: candidate applications, interviews
 * and recordings are always retained, and the confirmation step says so
 * explicitly with real counts. Purge is the sole hard delete, and the server
 * refuses it for anything but an empty draft.
 */
export function JobsManagement() {
  const queryClient = useQueryClient();
  const [confirm, confirmDialog] = useConfirm();

  const [statusFilter, setStatusFilter] = useState('OPEN,IN_PROGRESS');
  const [search, setSearch] = useState('');
  const [staleBefore, setStaleBefore] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  const [reason, setReason] = useState(CLOSE_REASONS[0].value);
  const [confirmText, setConfirmText] = useState('');
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['admin-jobs', statusFilter, search, staleBefore],
    queryFn: () =>
      adminApi.listJobs({
        status: statusFilter || undefined,
        search: search || undefined,
        staleBefore: staleBefore || undefined,
        limit: 200,
      }),
  });

  const jobs = data?.jobs ?? [];
  const selectedIds = useMemo(() => [...selected], [selected]);

  // Loaded only once a bulk action is pending, so the operator sees the real
  // blast radius before confirming rather than a guess.
  const { data: bulkImpact, isFetching: isLoadingImpact } = useQuery({
    queryKey: ['admin-jobs-bulk-impact', selectedIds],
    queryFn: () => adminApi.bulkJobImpact(selectedIds),
    enabled: pendingAction !== null && selectedIds.length > 0,
  });

  const resetSelection = () => {
    setSelected(new Set());
    setPendingAction(null);
    setConfirmText('');
  };

  const bulkMutation = useMutation({
    mutationFn: async (action: BulkAction) =>
      action === 'close'
        ? adminApi.bulkCloseJobs(selectedIds, reason)
        : adminApi.bulkArchiveJobs(selectedIds),
    onSuccess: (result: any, action) => {
      const done = action === 'close' ? result.closed : result.archived;
      const failed = result.failed?.length ?? 0;
      setResultMessage(
        `${done} job${done === 1 ? '' : 's'} ${action === 'close' ? 'closed' : 'archived'}` +
          (action === 'close' ? `, ${result.applicationsClosed} application(s) marked Job Closed` : '') +
          (failed > 0 ? ` — ${failed} failed` : '') +
          '. All candidate history retained.'
      );
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
      resetSelection();
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => adminApi.reopenJob(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-jobs'] }),
  });

  const purgeMutation = useMutation({
    mutationFn: (id: string) => adminApi.purgeJob(id),
    onSuccess: () => {
      setResultMessage('Draft job permanently deleted.');
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
    },
    onError: (err: any) => setResultMessage(err?.message ?? 'Purge refused.'),
  });

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((current) => (current.size === jobs.length ? new Set() : new Set(jobs.map((job) => job.id))));
  };

  const confirmWord = pendingAction === 'close' ? 'CLOSE' : 'ARCHIVE';
  const canConfirm = confirmText.trim().toUpperCase() === confirmWord && !bulkMutation.isPending;

  return (
    <div>
      {confirmDialog}
      <h1 className="text-xl font-semibold mb-1">Jobs</h1>
      <p className="text-sm text-gray-500 mb-6">
        Close or archive stale postings. Neither deletes anything — applications, interviews and
        recordings are always kept, and closed jobs can be reopened.
      </p>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Status</span>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="OPEN,IN_PROGRESS">Live (Open + In Progress)</option>
            <option value="OPEN">Open</option>
            <option value="HOLD">On Hold</option>
            <option value="DRAFT">Draft</option>
            <option value="CLOSED">Closed</option>
            <option value="ARCHIVED">Archived</option>
            <option value="">All</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Published before</span>
          <input
            type="date"
            value={staleBefore}
            onChange={(event) => setStaleBefore(event.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-sm flex-1 min-w-[220px]">
          <span className="mb-1 block font-medium text-gray-700">Search</span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Job title or company"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      {isFetching && <p className="mb-4 text-sm text-gray-500">Loading…</p>}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load jobs.
        </div>
      )}
      {resultMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{resultMessage}</span>
        </div>
      )}

      {selected.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="text-sm font-medium text-gray-700">{selected.size} selected</span>
          <button
            onClick={() => setPendingAction('close')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Ban className="h-4 w-4" /> Close selected
          </button>
          <button
            onClick={() => setPendingAction('archive')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Archive className="h-4 w-4" /> Archive selected
          </button>
          <button onClick={resetSelection} className="text-sm text-gray-500 hover:text-gray-700">
            Clear
          </button>
        </div>
      )}

      {pendingAction && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-4">
          <h2 className="text-sm font-semibold text-amber-900">
            {pendingAction === 'close' ? 'Close' : 'Archive'} {selected.size} job
            {selected.size === 1 ? '' : 's'}?
          </h2>

          {isLoadingImpact && <p className="mt-2 text-sm text-amber-800">Checking impact…</p>}

          {bulkImpact && (
            <ul className="mt-2 space-y-1 text-sm text-amber-900">
              {pendingAction === 'close' && (
                <li>
                  <strong>{bulkImpact.applicationsToClose}</strong> open application(s) will be
                  marked <em>Job Closed</em> (of {bulkImpact.applications} total).
                </li>
              )}
              <li>
                <strong>{bulkImpact.preserved.interviews}</strong> interview(s) and{' '}
                <strong>{bulkImpact.preserved.interviewRecordings}</strong> recording(s) are{' '}
                <strong>kept</strong>.
              </li>
              <li>Nothing is deleted. These jobs can be reopened.</li>
            </ul>
          )}

          {pendingAction === 'close' && (
            <label className="mt-3 block text-sm">
              <span className="mb-1 block font-medium text-amber-900">Reason</span>
              <select
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                className="rounded-lg border border-amber-300 px-3 py-2 text-sm"
              >
                {CLOSE_REASONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="mt-3 block text-sm">
            <span className="mb-1 block font-medium text-amber-900">
              Type <code className="font-mono">{confirmWord}</code> to confirm
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              className="rounded-lg border border-amber-300 px-3 py-2 text-sm"
            />
          </label>

          <div className="mt-4 flex gap-2">
            <button
              disabled={!canConfirm}
              onClick={() => bulkMutation.mutate(pendingAction)}
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bulkMutation.isPending
                ? 'Working…'
                : `${pendingAction === 'close' ? 'Close' : 'Archive'} ${selected.size} job${
                    selected.size === 1 ? '' : 's'
                  }`}
            </button>
            <button
              onClick={() => {
                setPendingAction(null);
                setConfirmText('');
              }}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={jobs.length > 0 && selected.size === jobs.length}
                  onChange={toggleAll}
                  aria-label="Select all jobs"
                />
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Title</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Company</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Org</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Applicants</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Interviews</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Published</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Closed</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {jobs.map((job) => (
              <tr key={job.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(job.id)}
                    onChange={() => toggle(job.id)}
                    aria-label={`Select ${job.title}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                      STATUS_STYLES[job.status]
                    }`}
                  >
                    {job.status}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-gray-900">{job.title}</td>
                <td className="px-4 py-3 text-gray-600">{job.companyName}</td>
                <td className="px-4 py-3 text-gray-600">{job.organization?.name ?? '—'}</td>
                <td className="px-4 py-3 text-right text-gray-700">{job._count.candidates}</td>
                <td className="px-4 py-3 text-right text-gray-700">{job._count.interviews}</td>
                <td className="px-4 py-3 text-gray-600">{formatDate(job.publishedAt)}</td>
                <td className="px-4 py-3 text-gray-600">{formatDate(job.closedAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {(job.status === 'CLOSED' || job.status === 'ARCHIVED') && (
                      <button
                        onClick={() => reopenMutation.mutate(job.id)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Reopen
                      </button>
                    )}
                    {job.status === 'DRAFT' && job._count.candidates === 0 && (
                      <button
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Permanently delete the draft "${job.title}"?`,
                              description:
                                'This cannot be undone. It is only allowed because the draft has no applications or interviews.',
                              confirmLabel: 'Delete draft',
                              destructive: true,
                              typeToConfirm: 'delete',
                            })
                          ) {
                            purgeMutation.mutate(job.id);
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Purge
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {jobs.length === 0 && !isFetching && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-500">
                  No jobs match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <p className="mt-3 text-xs text-gray-500">
          Showing {data.returned} of {data.total} matching jobs.
        </p>
      )}
    </div>
  );
}
